import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { Download, FileDown, FileEdit, FileText, UserCheck } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDateTime, Modal, ImageField } from "./_shared";


const SYMPOSIUM_TABS = [
  { id: "content", label: "Content", icon: FileEdit },
  { id: "abstracts", label: "Abstracts", icon: FileText },
  { id: "registrations", label: "Registrations", icon: UserCheck },
];

export function SymposiumAdminPanel() {
  const [tab, setTab] = useState("content");
  return (
    <div data-testid="symposium-admin">
      <p className="eyebrow mb-2">Symposium</p>
      <h1 className="font-serif-display text-4xl mb-6" style={{ color: "var(--ser-primary-dark)" }}>
        Symposium management
      </h1>
      <div className="flex flex-wrap gap-2 mb-8 border-b" style={{ borderColor: "var(--ser-border, #e5e5e5)" }}>
        {SYMPOSIUM_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className="px-4 py-2.5 text-sm font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 transition-colors -mb-px"
            style={{
              color: tab === t.id ? "var(--ser-primary)" : "var(--ser-text-muted)",
              borderColor: tab === t.id ? "var(--ser-primary)" : "transparent",
            }}
            data-testid={`symposium-tab-${t.id}`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>
      {tab === "content" && <SymposiumContentPanel />}
      {tab === "abstracts" && <AbstractsPanel />}
      {tab === "registrations" && <SymposiumRegistrationsPanel />}
    </div>
  );
}

function SymposiumContentPanel() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/symposium-content").then(({ data }) => setContent(data));
  }, []);

  if (!content) return <p>Loading…</p>;

  const update = (k, v) => setContent({ ...content, [k]: v });
  const updateList = (key, idx, k, v) => {
    const list = [...(content[key] || [])];
    list[idx] = { ...list[idx], [k]: v };
    setContent({ ...content, [key]: list });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/symposium-content", content);
      toast.success("Symposium content saved");
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="symposium-content-form">
      <div className="ser-card p-6">
        <h2 className="font-serif-display text-2xl mb-5" style={{ color: "var(--ser-primary-dark)" }}>Headline</h2>
        <div className="space-y-4">
          <div><label className="ser-label">Symposium name</label>
            <input className="ser-input" value={content.name} onChange={(e) => update("name", e.target.value)} data-testid="sym-name" />
          </div>
          <div><label className="ser-label">Tagline</label>
            <input className="ser-input" value={content.tagline} onChange={(e) => update("tagline", e.target.value)} data-testid="sym-tagline" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="ser-label">Dates</label>
              <input className="ser-input" value={content.dates} onChange={(e) => update("dates", e.target.value)} data-testid="sym-dates" />
            </div>
            <div><label className="ser-label">Abstract deadline (ISO)</label>
              <input type="date" className="ser-input" value={content.abstract_deadline?.slice(0, 10) || ""} onChange={(e) => update("abstract_deadline", e.target.value)} data-testid="sym-abstract-deadline" />
            </div>
            <div><label className="ser-label">Venue name</label>
              <input className="ser-input" value={content.venue_name} onChange={(e) => update("venue_name", e.target.value)} data-testid="sym-venue-name" />
            </div>
            <div><label className="ser-label">Venue location</label>
              <input className="ser-input" value={content.venue_location} onChange={(e) => update("venue_location", e.target.value)} data-testid="sym-venue-location" />
            </div>
          </div>
          <ImageField label="Hero image" value={content.hero_image} onChange={(v) => update("hero_image", v)} testIdPrefix="sym-hero-image" />
        </div>
      </div>

      <div className="ser-card p-6">
        <h2 className="font-serif-display text-2xl mb-5" style={{ color: "var(--ser-primary-dark)" }}>Body copy</h2>
        <div className="space-y-4">
          <div><label className="ser-label">About</label>
            <textarea rows={4} className="ser-input" value={content.about_body} onChange={(e) => update("about_body", e.target.value)} data-testid="sym-about" />
          </div>
          <div><label className="ser-label">Programme</label>
            <textarea rows={3} className="ser-input" value={content.programme_body} onChange={(e) => update("programme_body", e.target.value)} data-testid="sym-programme" />
          </div>
          <div><label className="ser-label">Abstract guidelines</label>
            <textarea rows={6} className="ser-input" value={content.abstract_guidelines} onChange={(e) => update("abstract_guidelines", e.target.value)} data-testid="sym-guidelines" />
          </div>
          <div className="p-4 rounded" style={{ backgroundColor: "var(--ser-section-alt, #f5f5f0)" }}>
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={!!content.abstracts_closed}
                onChange={(e) => update("abstracts_closed", e.target.checked)}
                data-testid="sym-abstracts-closed-toggle"
              />
              <span className="text-sm font-bold uppercase tracking-wider" style={{ color: "var(--ser-teal)" }}>
                Close abstract submissions
              </span>
            </label>
            <p className="text-xs mb-2" style={{ color: "var(--ser-text-muted)" }}>
              When ON, the public submission form is replaced by the message below and new submissions are rejected (HTTP 409). Uses double line breaks between paragraphs.
            </p>
            <label className="ser-label">Submission-closed message</label>
            <textarea
              rows={10}
              className="ser-input font-mono text-xs"
              value={content.abstracts_closed_message || ""}
              onChange={(e) => update("abstracts_closed_message", e.target.value)}
              data-testid="sym-abstracts-closed-message"
            />
          </div>
          <div><label className="ser-label">Payment instructions</label>
            <textarea rows={3} className="ser-input" value={content.payment_instructions} onChange={(e) => update("payment_instructions", e.target.value)} data-testid="sym-payment" />
          </div>
          <div><label className="ser-label">Banking details</label>
            <textarea rows={3} className="ser-input" value={content.banking_details} onChange={(e) => update("banking_details", e.target.value)} data-testid="sym-banking" />
          </div>
          <div><label className="ser-label">Accommodation text</label>
            <textarea rows={2} className="ser-input" value={content.accommodation_text} onChange={(e) => update("accommodation_text", e.target.value)} data-testid="sym-accom" />
          </div>
          <div><label className="ser-label">Transport / shuttle text</label>
            <textarea rows={4} className="ser-input" value={content.transport_text} onChange={(e) => update("transport_text", e.target.value)} data-testid="sym-transport" />
          </div>
          <div><label className="ser-label">Refund note</label>
            <textarea rows={2} className="ser-input" value={content.refund_note} onChange={(e) => update("refund_note", e.target.value)} data-testid="sym-refund-note" />
          </div>
        </div>
      </div>

      <div className="ser-card p-6">
        <h2 className="font-serif-display text-2xl mb-5" style={{ color: "var(--ser-primary-dark)" }}>Fees</h2>
        <p className="text-xs mb-4" style={{ color: "var(--ser-text-muted)" }}>
          Three tiers — Early bird, Standard, Late. Dates use ISO format YYYY-MM-DD. The active tier is chosen by today's date when someone registers.
        </p>
        <div className="space-y-4">
          {content.fees.map((f, idx) => (
            <div key={f.tier} className="p-4 rounded" style={{ backgroundColor: "var(--ser-section-alt, #f5f5f0)" }} data-testid={`fee-row-${f.tier}`}>
              <p className="eyebrow mb-3">{f.label}</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <div><label className="ser-label">Label</label>
                  <input className="ser-input" value={f.label} onChange={(e) => updateList("fees", idx, "label", e.target.value)} />
                </div>
                <div><label className="ser-label">Period text</label>
                  <input className="ser-input" value={f.period} onChange={(e) => updateList("fees", idx, "period", e.target.value)} />
                </div>
                <div></div>
                <div><label className="ser-label">Start (YYYY-MM-DD)</label>
                  <input className="ser-input" placeholder="empty = no lower bound" value={f.start || ""} onChange={(e) => updateList("fees", idx, "start", e.target.value || null)} />
                </div>
                <div><label className="ser-label">End (YYYY-MM-DD)</label>
                  <input className="ser-input" value={f.end || ""} onChange={(e) => updateList("fees", idx, "end", e.target.value || null)} />
                </div>
                <div></div>
                <div><label className="ser-label">SER member (ZAR)</label>
                  <input type="number" className="ser-input" value={f.ser} onChange={(e) => updateList("fees", idx, "ser", parseInt(e.target.value, 10) || 0)} />
                </div>
                <div><label className="ser-label">Non-SER (ZAR)</label>
                  <input type="number" className="ser-input" value={f.non_ser} onChange={(e) => updateList("fees", idx, "non_ser", parseInt(e.target.value, 10) || 0)} />
                </div>
                <div><label className="ser-label">Student (ZAR)</label>
                  <input type="number" className="ser-input" value={f.student} onChange={(e) => updateList("fees", idx, "student", parseInt(e.target.value, 10) || 0)} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="ser-card p-6">
        <h2 className="font-serif-display text-2xl mb-5" style={{ color: "var(--ser-primary-dark)" }}>Accommodation links</h2>
        <div className="space-y-3">
          {content.accommodation_links.map((l, idx) => (
            // eslint-disable-next-line react/no-array-index-key -- editable list: stable index avoids input remount on value edits
            <div key={idx} className="grid sm:grid-cols-2 gap-3">
              <input className="ser-input" placeholder="Label" value={l.label} onChange={(e) => updateList("accommodation_links", idx, "label", e.target.value)} />
              <input className="ser-input" placeholder="URL" value={l.url} onChange={(e) => updateList("accommodation_links", idx, "url", e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      <div className="ser-card p-6">
        <h2 className="font-serif-display text-2xl mb-5" style={{ color: "var(--ser-primary-dark)" }}>Refund policy windows</h2>
        <div className="space-y-3">
          {content.refund_policy.map((r, idx) => (
            // eslint-disable-next-line react/no-array-index-key -- editable list: stable index avoids input remount on value edits
            <div key={idx} className="grid sm:grid-cols-2 gap-3">
              <input className="ser-input" placeholder="Window" value={r.window} onChange={(e) => updateList("refund_policy", idx, "window", e.target.value)} />
              <input className="ser-input" placeholder="Refund description" value={r.refund} onChange={(e) => updateList("refund_policy", idx, "refund", e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      <div className="ser-card p-6">
        <h2 className="font-serif-display text-2xl mb-5" style={{ color: "var(--ser-primary-dark)" }}>Co-chairs</h2>
        <div className="space-y-3">
          {content.co_chairs.map((c, idx) => (
            // eslint-disable-next-line react/no-array-index-key -- editable list: stable index avoids input remount on value edits
            <div key={idx} className="grid sm:grid-cols-3 gap-3">
              <input className="ser-input" placeholder="Role" value={c.role} onChange={(e) => updateList("co_chairs", idx, "role", e.target.value)} />
              <input className="ser-input" placeholder="Name" value={c.name} onChange={(e) => updateList("co_chairs", idx, "name", e.target.value)} />
              <input className="ser-input" placeholder="Email" value={c.email} onChange={(e) => updateList("co_chairs", idx, "email", e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      <div className="ser-card p-6">
        <h2 className="font-serif-display text-2xl mb-2" style={{ color: "var(--ser-primary-dark)" }}>Partners</h2>
        <p className="text-xs mb-4" style={{ color: "var(--ser-text-muted)" }}>
          Logos shown on the symposium page under the "Implemented in partnership with" strip. Use the upload button to add a new logo from your device.
        </p>
        <div className="mb-4">
          <label className="ser-label">Heading text</label>
          <input className="ser-input" value={content.partners_heading || ""}
            onChange={(e) => update("partners_heading", e.target.value)}
            data-testid="sym-partners-heading" />
        </div>
        <div className="space-y-4">
          {(content.partners || []).map((p, idx) => (
            // eslint-disable-next-line react/no-array-index-key -- editable list: stable index avoids input remount on value edits
            <div key={idx} className="p-4 rounded grid sm:grid-cols-2 gap-3"
              style={{ backgroundColor: "var(--ser-section-alt, #f5f5f0)" }}
              data-testid={`partner-row-${idx}`}>
              <div>
                <label className="ser-label">Name</label>
                <input className="ser-input"
                  value={p.name || ""}
                  onChange={(e) => updateList("partners", idx, "name", e.target.value)}
                  data-testid={`sym-partner-${idx}-name`} />
                <label className="ser-label mt-3">Website (optional)</label>
                <input className="ser-input" placeholder="https://…"
                  value={p.website || ""}
                  onChange={(e) => updateList("partners", idx, "website", e.target.value)}
                  data-testid={`sym-partner-${idx}-url`} />
                <button type="button"
                  className="btn-ser-ghost mt-3 text-xs"
                  onClick={() => {
                    const list = content.partners.filter((_, i) => i !== idx);
                    setContent({ ...content, partners: list });
                  }}
                  data-testid={`sym-partner-${idx}-remove`}>
                  Remove partner
                </button>
              </div>
              <div>
                <ImageField
                  label="Logo image"
                  value={p.logo_url}
                  onChange={(v) => updateList("partners", idx, "logo_url", v)}
                  testIdPrefix={`sym-partner-${idx}-logo`}
                />
              </div>
            </div>
          ))}
          <button type="button" className="btn-ser-outline"
            onClick={() => setContent({
              ...content,
              partners: [...(content.partners || []), { name: "", logo_url: "", website: "" }],
            })}
            data-testid="sym-partner-add">
            + Add partner
          </button>
        </div>
      </div>

      <button type="submit" disabled={saving} className="btn-ser-primary" data-testid="save-symposium-btn">
        {saving ? "Saving…" : "Save symposium content"}
      </button>
    </form>
  );
}

function AbstractsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/symposium/abstracts").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((a) => a.status === filter);
  }, [items, filter]);

  const update = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/symposium/abstracts/${editing.abstract_id}`, {
        status: editing.status,
        assigned_type: editing.assigned_type || editing.presentation_type,
        review_notes: editing.review_notes || "",
      });
      toast.success("Abstract updated");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const togglePublish = async (a, next) => {
    try {
      const { data } = await api.patch(`/admin/symposium/abstracts/${a.abstract_id}/publish`, { is_public: next });
      toast.success(next ? "Published to public programme" : "Removed from public programme");
      setItems((prev) => prev.map((it) => it.abstract_id === a.abstract_id ? { ...it, ...data } : it));
      if (editing && editing.abstract_id === a.abstract_id) {
        setEditing({ ...editing, is_public: data.is_public, published_at: data.published_at });
      }
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const exportCSV = () => {
    const rows = [
      ["abstract_id","status","is_public","assigned_type","honorific","name","surname","email","institution","abstract_title","presentation_type","is_student","keywords","word_count","co_authors","created_at"],
      ...items.map((a) => [
        a.abstract_id, a.status, a.is_public ? "yes" : "no", a.assigned_type || "", a.title, a.name, a.surname, a.email,
        a.institution, a.abstract_title || "", a.presentation_type, a.is_student ? "yes" : "no", a.keywords, a.word_count,
        (a.co_authors || "").replace(/[\r\n]+/g, " "), a.created_at,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `abstracts-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    doc.setFontSize(14);
    doc.text("SER Africa Symposium — Abstracts", 40, 36);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Exported ${new Date().toLocaleString("en-ZA")} · ${items.length} abstract(s)`, 40, 52);
    doc.setTextColor(0);
    autoTable(doc, {
      startY: 66,
      head: [["Submitted", "Presenter", "Title", "Institution", "Requested", "Assigned", "Words", "Student", "Status"]],
      body: items.map((a) => [
        formatDateTime(a.created_at),
        `${a.title} ${a.name} ${a.surname}`,
        a.abstract_title || "—",
        a.institution,
        a.presentation_type,
        a.assigned_type || "—",
        String(a.word_count ?? ""),
        a.is_student ? "Yes" : "No",
        a.status,
      ]),
      styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
      headStyles: { fillColor: [79, 124, 46], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [244, 241, 234] },
      columnStyles: { 1: { cellWidth: 100 }, 2: { cellWidth: 150 }, 3: { cellWidth: 110 } },
      margin: { left: 40, right: 40 },
    });
    doc.save(`abstracts-${Date.now()}.pdf`);
  };

  return (
    <div data-testid="abstracts-panel">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        {["all", "pending", "accepted", "rejected"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className="px-4 py-1.5 rounded-full text-xs font-semibold capitalize"
            style={{
              backgroundColor: filter === s ? "var(--ser-primary)" : "var(--ser-section-alt, #f5f5f0)",
              color: filter === s ? "white" : "var(--ser-text-muted)",
            }} data-testid={`abs-filter-${s}`}>{s}</button>
        ))}
        <button onClick={exportCSV} className="btn-ser-ghost ml-auto" data-testid="abs-export-csv">
          <Download className="w-4 h-4" /> CSV
        </button>
        <button onClick={exportPDF} className="btn-ser-ghost" data-testid="abs-export-pdf">
          <FileDown className="w-4 h-4" /> PDF
        </button>
      </div>

      {loading ? <p style={{ color: "var(--ser-text-muted)" }}>Loading…</p> :
       filtered.length === 0 ? <p className="py-8 text-center" style={{ color: "var(--ser-text-muted)" }}>No abstracts.</p> : (
        <div className="ser-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ backgroundColor: "var(--ser-section-alt, #f5f5f0)" }}>
                <tr style={{ color: "var(--ser-teal)" }}>
                  <th className="p-3 text-left text-xs uppercase tracking-wider font-bold">Submitted</th>
                  <th className="p-3 text-left text-xs uppercase tracking-wider font-bold">Presenter</th>
                  <th className="p-3 text-left text-xs uppercase tracking-wider font-bold">Title</th>
                  <th className="p-3 text-left text-xs uppercase tracking-wider font-bold">Institution</th>
                  <th className="p-3 text-left text-xs uppercase tracking-wider font-bold">Requested</th>
                  <th className="p-3 text-left text-xs uppercase tracking-wider font-bold">Words</th>
                  <th className="p-3 text-left text-xs uppercase tracking-wider font-bold">Status</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.abstract_id} className="border-t" style={{ borderColor: "var(--ser-border, #e5e5e5)" }}
                    data-testid={`abs-row-${a.abstract_id}`}>
                    <td className="p-3 text-xs" style={{ color: "var(--ser-text-muted)" }}>{formatDateTime(a.created_at)}</td>
                    <td className="p-3">
                      {a.title} {a.name} {a.surname}{a.is_student && <span className="ser-tag ml-2">Student</span>}
                      <div className="text-xs" style={{ color: "var(--ser-text-muted)" }}>{a.email}</div>
                    </td>
                    <td className="p-3 text-sm max-w-xs">
                      <span className="line-clamp-2" title={a.abstract_title || ""}>{a.abstract_title || "—"}</span>
                    </td>
                    <td className="p-3 text-sm" style={{ color: "var(--ser-text-muted)" }}>{a.institution}</td>
                    <td className="p-3"><span className="ser-tag">{a.presentation_type}</span></td>
                    <td className="p-3 text-xs" style={{ color: "var(--ser-text-muted)" }}>{a.word_count}</td>
                    <td className="p-3">
                      <span className="ser-tag" style={
                        a.status === "accepted" ? { backgroundColor: "var(--ser-primary)" } :
                        a.status === "rejected" ? { backgroundColor: "var(--ser-clay-dark)" } :
                        { backgroundColor: "var(--ser-text-muted)" }
                      }>{a.status}</span>
                      {a.assigned_type && a.status === "accepted" && (
                        <div className="text-xs mt-1" style={{ color: "var(--ser-text-muted)" }}>→ {a.assigned_type}</div>
                      )}
                      {a.status === "accepted" && (
                        <div className="mt-1">
                          {a.is_public ? (
                            <span
                              className="text-[0.62rem] font-bold uppercase tracking-wider px-1.5 py-0.5"
                              style={{ backgroundColor: "var(--ser-teal)", color: "white" }}
                              data-testid={`abs-public-${a.abstract_id}`}
                            >
                              Public
                            </span>
                          ) : (
                            <span
                              className="text-[0.62rem] font-bold uppercase tracking-wider px-1.5 py-0.5"
                              style={{ backgroundColor: "var(--ser-sand)", color: "var(--ser-text-muted)" }}
                              data-testid={`abs-draft-${a.abstract_id}`}
                            >
                              Draft
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <button onClick={() => setEditing({ ...a })} className="btn-ser-ghost text-xs"
                        data-testid={`review-abs-${a.abstract_id}`}>Review</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
       )}

      {editing && (
        <Modal onClose={() => setEditing(null)} title={`Review · ${editing.name} ${editing.surname}`} wide>
          <div className="space-y-4 mb-5">
            <div>
              <p className="eyebrow mb-1">Abstract title</p>
              <h3 className="font-serif-display text-xl leading-snug" style={{ color: "var(--ser-primary-dark)" }}>
                {editing.abstract_title || <span style={{ color: "var(--ser-text-muted)" }}>— untitled —</span>}
              </h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div><strong>Email:</strong> {editing.email}</div>
              <div><strong>Institution:</strong> {editing.institution}</div>
              <div><strong>Requested type:</strong> {editing.presentation_type}</div>
              <div><strong>Student:</strong> {editing.is_student ? "Yes" : "No"}</div>
              <div className="sm:col-span-2"><strong>Co-authors:</strong> {editing.co_authors || "—"}</div>
              <div className="sm:col-span-2"><strong>Keywords:</strong> {editing.keywords}</div>
            </div>
            <div className="ser-card p-4" style={{ backgroundColor: "var(--ser-section-alt, #f5f5f0)" }}>
              <p className="eyebrow mb-2">Abstract ({editing.word_count} words)</p>
              <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: "var(--ser-text-muted)" }}>{editing.abstract_body}</p>
            </div>
          </div>
          <form onSubmit={update} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="ser-label">Decision</label>
                <select className="ser-input" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="abs-decision">
                  <option value="pending">Pending</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="ser-label">Assigned format</label>
                <select className="ser-input"
                  value={editing.assigned_type || editing.presentation_type}
                  onChange={(e) => setEditing({ ...editing, assigned_type: e.target.value })}
                  data-testid="abs-assigned-type">
                  <option>Standard</option>
                  <option>Speed</option>
                  <option>Poster</option>
                </select>
              </div>
            </div>
            <div>
              <label className="ser-label">Internal review notes</label>
              <textarea rows={3} className="ser-input" value={editing.review_notes || ""} onChange={(e) => setEditing({ ...editing, review_notes: e.target.value })} data-testid="abs-notes" />
            </div>
            {editing.status === "accepted" && (
              <div className="ser-card p-4 flex items-start gap-3"
                style={{ backgroundColor: "var(--ser-section-alt)", borderLeft: "3px solid var(--ser-primary)" }}>
                <div className="flex-1">
                  <p className="eyebrow mb-1">Public programme</p>
                  <p className="text-sm mb-2" style={{ color: "var(--ser-text-muted)" }}>
                    {editing.is_public
                      ? "This abstract is LIVE on the public programme page."
                      : "Accepted but not yet visible on the public programme page. Publish to make it visible to visitors."}
                  </p>
                  {editing.published_at && (
                    <p className="text-xs" style={{ color: "var(--ser-text-muted)" }}>
                      Last published: {formatDateTime(editing.published_at)}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => togglePublish(editing, !editing.is_public)}
                  className={editing.is_public ? "btn-ser-outline" : "btn-ser-primary"}
                  data-testid="abs-publish-toggle"
                >
                  {editing.is_public ? "Unpublish" : "Publish to programme"}
                </button>
              </div>
            )}
            <div className="flex gap-3">
              <button type="submit" className="btn-ser-primary" data-testid="save-abs-decision">Save decision</button>
              <button type="button" onClick={() => setEditing(null)} className="btn-ser-outline">Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function SymposiumRegistrationsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/symposium/registrations").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    if (filter === "shuttle") return items.filter((r) => r.shuttle_needed);
    return items.filter((r) => r.payment_status === filter);
  }, [items, filter]);

  const update = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/symposium/registrations/${editing.registration_id}/payment`, {
        payment_status: editing.payment_status,
        admin_notes: editing.admin_notes || "",
      });
      toast.success("Registration updated");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const exportCSV = () => {
    const rows = [
      ["registration_id","payment_status","title","name","surname","email","mobile","institution","ser_membership","is_student","fee_tier","fee_amount_zar","abstract_submitted","dietary","dietary_other","shuttle_needed","invoice_address","created_at"],
      ...items.map((r) => [
        r.registration_id, r.payment_status, r.title, r.name, r.surname, r.email, r.mobile, r.institution,
        r.ser_membership, r.is_student ? "yes" : "no", r.fee_tier_label, r.fee_amount_zar,
        r.abstract_submitted ? "yes" : "no", r.dietary, r.dietary_other || "",
        r.shuttle_needed ? "yes" : "no", (r.invoice_address || "").replace(/[\r\n]+/g, " "), r.created_at,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `symposium-registrations-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    doc.setFontSize(14);
    doc.text("SER Africa Symposium — Registrations", 40, 36);
    doc.setFontSize(9);
    doc.setTextColor(100);
    const totalDueLocal = items
      .filter((r) => r.payment_status === "pending" || r.payment_status === "proof_received")
      .reduce((s, r) => s + (r.fee_amount_zar || 0), 0);
    const shuttleLocal = items.filter((r) => r.shuttle_needed).length;
    doc.text(
      `Exported ${new Date().toLocaleString("en-ZA")} · ${items.length} registration(s) · Shuttle: ${shuttleLocal} · Outstanding: ZAR ${totalDueLocal.toLocaleString("en-ZA")}`,
      40, 52,
    );
    doc.setTextColor(0);
    autoTable(doc, {
      startY: 66,
      head: [["When", "Delegate", "Email", "Mobile", "Institution", "SER", "Stu.", "Tier", "Fee (ZAR)", "Diet", "Shut.", "Payment"]],
      body: items.map((r) => [
        formatDateTime(r.created_at),
        `${r.title} ${r.name} ${r.surname}`,
        r.email,
        r.mobile,
        r.institution,
        r.ser_membership === "SER member" ? "Yes" : "No",
        r.is_student ? "Yes" : "No",
        r.fee_tier_label || "",
        Number(r.fee_amount_zar || 0).toLocaleString("en-ZA"),
        r.dietary === "Other" ? `Other: ${r.dietary_other}` : r.dietary,
        r.shuttle_needed ? "Yes" : "No",
        (r.payment_status || "").replace("_", " "),
      ]),
      styles: { fontSize: 7.5, cellPadding: 3, overflow: "linebreak" },
      headStyles: { fillColor: [79, 124, 46], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [244, 241, 234] },
      columnStyles: { 1: { cellWidth: 95 }, 2: { cellWidth: 120 }, 4: { cellWidth: 95 } },
      margin: { left: 40, right: 40 },
    });
    doc.save(`symposium-registrations-${Date.now()}.pdf`);
  };

  const shuttleCount = items.filter((r) => r.shuttle_needed).length;
  const totalDue = items
    .filter((r) => r.payment_status === "pending" || r.payment_status === "proof_received")
    .reduce((s, r) => s + (r.fee_amount_zar || 0), 0);

  return (
    <div data-testid="symposium-registrations-panel">
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="ser-card p-5">
          <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: "var(--ser-text-muted)" }}>Registrations</p>
          <p className="text-3xl font-serif-display" style={{ color: "var(--ser-teal)" }}>{items.length}</p>
        </div>
        <div className="ser-card p-5">
          <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: "var(--ser-text-muted)" }}>Shuttle requests</p>
          <p className="text-3xl font-serif-display" style={{ color: "var(--ser-primary)" }}>{shuttleCount}</p>
        </div>
        <div className="ser-card p-5">
          <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: "var(--ser-text-muted)" }}>Outstanding (ZAR)</p>
          <p className="text-3xl font-serif-display" style={{ color: "var(--ser-clay-dark)" }}>{totalDue.toLocaleString("en-ZA")}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {["all", "pending", "proof_received", "paid", "refunded", "cancelled", "shuttle"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className="px-3 py-1.5 rounded-full text-xs font-semibold capitalize"
            style={{
              backgroundColor: filter === s ? "var(--ser-primary)" : "var(--ser-section-alt, #f5f5f0)",
              color: filter === s ? "white" : "var(--ser-text-muted)",
            }} data-testid={`reg-filter-${s}`}>{s.replace("_", " ")}</button>
        ))}
        <button onClick={exportCSV} className="btn-ser-ghost ml-auto" data-testid="reg-export-csv">
          <Download className="w-4 h-4" /> CSV
        </button>
        <button onClick={exportPDF} className="btn-ser-ghost" data-testid="reg-export-pdf">
          <FileDown className="w-4 h-4" /> PDF
        </button>
      </div>

      {loading ? <p style={{ color: "var(--ser-text-muted)" }}>Loading…</p> :
       filtered.length === 0 ? <p className="py-8 text-center" style={{ color: "var(--ser-text-muted)" }}>No registrations.</p> : (
        <div className="ser-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ backgroundColor: "var(--ser-section-alt, #f5f5f0)" }}>
                <tr style={{ color: "var(--ser-teal)" }}>
                  <th className="p-3 text-left text-xs uppercase tracking-wider font-bold">When</th>
                  <th className="p-3 text-left text-xs uppercase tracking-wider font-bold">Delegate</th>
                  <th className="p-3 text-left text-xs uppercase tracking-wider font-bold">Institution</th>
                  <th className="p-3 text-left text-xs uppercase tracking-wider font-bold">Tier</th>
                  <th className="p-3 text-left text-xs uppercase tracking-wider font-bold">Fee</th>
                  <th className="p-3 text-left text-xs uppercase tracking-wider font-bold">Shuttle</th>
                  <th className="p-3 text-left text-xs uppercase tracking-wider font-bold">Payment</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.registration_id} className="border-t" style={{ borderColor: "var(--ser-border, #e5e5e5)" }}
                    data-testid={`reg-row-${r.registration_id}`}>
                    <td className="p-3 text-xs" style={{ color: "var(--ser-text-muted)" }}>{formatDateTime(r.created_at)}</td>
                    <td className="p-3">
                      {r.title} {r.name} {r.surname}
                      {r.is_student && <span className="ser-tag ml-2">Student</span>}
                      <div className="text-xs" style={{ color: "var(--ser-text-muted)" }}>{r.email} · {r.mobile}</div>
                    </td>
                    <td className="p-3 text-sm" style={{ color: "var(--ser-text-muted)" }}>{r.institution}</td>
                    <td className="p-3"><span className="ser-tag">{r.fee_tier_label}</span></td>
                    <td className="p-3 text-sm font-bold" style={{ color: "var(--ser-teal)" }}>ZAR {Number(r.fee_amount_zar).toLocaleString("en-ZA")}</td>
                    <td className="p-3 text-xs">{r.shuttle_needed ? <span className="ser-tag">Yes</span> : "—"}</td>
                    <td className="p-3">
                      <span className="ser-tag" style={
                        r.payment_status === "paid" ? { backgroundColor: "var(--ser-primary)" } :
                        r.payment_status === "refunded" || r.payment_status === "cancelled" ? { backgroundColor: "var(--ser-text-muted)" } :
                        r.payment_status === "proof_received" ? { backgroundColor: "var(--ser-clay)" } :
                        { backgroundColor: "var(--ser-clay-dark)" }
                      }>{r.payment_status.replace("_", " ")}</span>
                    </td>
                    <td className="p-3 text-right">
                      <button onClick={() => setEditing({ ...r })} className="btn-ser-ghost text-xs"
                        data-testid={`edit-reg-${r.registration_id}`}>Manage</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
       )}

      {editing && (
        <Modal onClose={() => setEditing(null)} title={`Manage · ${editing.name} ${editing.surname}`} wide>
          <div className="grid sm:grid-cols-2 gap-3 text-sm mb-5">
            <div><strong>Reference:</strong> <code>{editing.registration_id}</code></div>
            <div><strong>Email:</strong> {editing.email}</div>
            <div><strong>Mobile:</strong> {editing.mobile}</div>
            <div><strong>Institution:</strong> {editing.institution}</div>
            <div><strong>SER status:</strong> {editing.ser_membership}</div>
            <div><strong>Student:</strong> {editing.is_student ? "Yes" : "No"}</div>
            <div><strong>Tier:</strong> {editing.fee_tier_label}</div>
            <div><strong>Fee:</strong> ZAR {Number(editing.fee_amount_zar).toLocaleString("en-ZA")}</div>
            <div><strong>Dietary:</strong> {editing.dietary}{editing.dietary === "Other" ? `: ${editing.dietary_other}` : ""}</div>
            <div><strong>Shuttle:</strong> {editing.shuttle_needed ? "Yes" : "No"}</div>
            <div><strong>Abstract submitted:</strong> {editing.abstract_submitted ? "Yes" : "No"}</div>
          </div>
          <div className="ser-card p-3 mb-5" style={{ backgroundColor: "var(--ser-section-alt, #f5f5f0)" }}>
            <p className="eyebrow mb-1">Invoice address</p>
            <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--ser-text-muted)" }}>{editing.invoice_address}</p>
          </div>
          <form onSubmit={update} className="space-y-4">
            <div>
              <label className="ser-label">Payment status</label>
              <select className="ser-input" value={editing.payment_status}
                onChange={(e) => setEditing({ ...editing, payment_status: e.target.value })} data-testid="reg-status">
                <option value="pending">Pending payment</option>
                <option value="proof_received">Proof of payment received</option>
                <option value="paid">Paid (confirmed)</option>
                <option value="refunded">Refunded</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="ser-label">Admin notes</label>
              <textarea rows={3} className="ser-input" value={editing.admin_notes || ""}
                onChange={(e) => setEditing({ ...editing, admin_notes: e.target.value })} data-testid="reg-notes" />
            </div>
            <div className="flex gap-3">
              <button type="submit" className="btn-ser-primary" data-testid="save-reg-status">Save</button>
              <button type="button" onClick={() => setEditing(null)} className="btn-ser-outline">Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

